import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Plus } from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../_shared/hooks';
import { getSequence, getSequenceUsage } from '../api-client';
import { isVersionConflictError } from '../conflict-errors';
import { duplicateSequenceWithEditedSteps } from '../duplicate-sequence-copy';
import {
  clearGeneratedCopy,
  decideStagedStep,
  editStagedStep,
  selectGeneratedCopyEntry,
  useGeneratedCopyStore,
} from '../generated-copy-store';
import { closeGenerateSession, selectIsSessionOpenFor, useGenerateWithAIStore } from '../generate-with-ai-store';
import { gongStepBodyFormat, isReplyAtOrdinal, sortStepsByPosition, stepOrdinal } from '../gong-flow-picker-api';
import { isSharedMappingConflictError, saveSequenceSteps } from '../mutations';
import { libraryQueryKey, sequenceDetailQueryKey, sequenceUsageQueryKey } from '../query-keys';
// QA round 8, item 6 — Inflection is capped at one step; the "Add step"
// button hides at the cap (belt-and-suspenders with the server's own 400).
import { INFLECTION_MAX_STEPS } from '../step-content-rules';
import type {
  LauncherChannel,
  PlaybooksGenerationInputs,
  PlaybooksSaveSequenceStepsResponse,
  PlaybooksSequenceDetailResponse,
  PlaybooksSequenceSummary,
  PlaybooksTemplatePlaybook,
  PlaybooksUsagePlace,
  PlaybooksUsagePlay,
  SequenceStep,
  SequenceSteps,
} from '../schemas';
import { clearCopyDraftIfUnchanged, copyDraftKey } from '../sequence-copy-draft-store';
import { useGongReplyStepOrders } from '../use-gong-reply-step-orders';
import { distinctUnsentPlays, libraryUsageGateNeedsConfirmation } from '../usage-gate';
import { useUsageSidebarDecision } from '../use-usage-sidebar-decision';
import { isUsageSidebarOpened, UsageSidebarOpenedError } from '../usage-sidebar-errors';
import { resumeReviewFromPending } from '../usage-sidebar-resume';
import { openUsageSidebarFromUsage, originKeyOf, useUsageSidebarStore } from '../usage-sidebar-store';
import { GenerateCampaignPicker } from './GenerateCampaignPicker';
import { GenerateWithAIDrawer } from './GenerateWithAIDrawer';
import { SequenceStepEditor } from './SequenceStepEditor';

/**
 * Launcher V2 QA-fixes E1 step 2 — the Config/Library page's direct sequence-
 * content editor. Genuine gap, not a scope cut: the backend (`createSequence`
 * / `getSequence` / `saveSequenceSteps`) has been fully built and tested since
 * PR C, with zero UI callers anywhere in the app until this unit.
 *
 * Rendered via `PlaybooksConfigPage.tsx`'s existing `RowShell.expand` prop
 * (the same mechanism Segmentation rows already use for their inline
 * conditions editor) — a caret on the Sequences row toggles this open in
 * place, never a drawer/sidebar.
 *
 * Fetches through the SAME query key `SequencesStep.tsx`'s `SequenceEditorShell`
 * uses (`sequenceDetailQueryKey(sequenceId)`), so the cache is shared —
 * expanding a Sequence here after (or before) editing it from inside a Play's
 * Sequences step sees the same data, no duplicate fetch, no stale-write race
 * between the two surfaces. `onSave` mirrors that shell's `handleSaveStep`
 * main-line branch exactly (whole-snapshot `saveSequenceSteps`, cache write on
 * success, invalidate-and-rethrow on a 409 `version_conflict`) — MINUS the
 * branch-vs-main-line routing that file's `handleSaveStep` also does: this
 * page has no Play/branch concept at all (D1 is play-scoped), so every save
 * from here is main-line, exactly like `SequencesStep`'s own main-line branch
 * when the sequence is sole-use.
 *
 * "Add step" is new here (the play-flow editor never adds a step — Qualify's
 * segmentation/sequence identities are already seeded with content by the
 * time a rep reaches them): `POST /v2/sequences/:id/versions` is
 * whole-snapshot (Key Decision 12), so "add a step" is simply "append a
 * default step to the current head steps and save the resulting array" —
 * there is no separate add-step endpoint to call instead. Never offered on
 * `gong_engage` (U5, 2026-09-17).
 *
 * U9 (terra-l2ai-epic-076.15) adds a row trigger + `GenerateCampaignPicker`
 * (this page's own campaign context, resolved by the rep rather than
 * inherited from a rung) and mounts `GenerateWithAIDrawer` (U7b) below,
 * unchanged, via its `campaignPicker` slot — see that drawer's own mount
 * further down this file.
 *
 * ── EXPLICITLY DEFERRED FROM THIS PANE (plan Group E, E1 step 2) ───────────
 * Flagged rather than half-built:
 *  - Token preview against a sample member — same reason: no roster/campaign
 *    here to interpolate against.
 *  - "Use flow default" — a Gong-flow-scaffold affordance, not built anywhere
 *    yet outside the play-flow editor's own Gong lock.
 *  - "Unpublished edit" badge / Revert — this pane always edits (and saves
 *    into) the sequence's own head directly; there is no branch/pin concept
 *    on this page for a badge to describe.
 *  - Admin "Preview impact of vN" — needs campaign-member-matching context
 *    this page does not have wired (same reasoning `PlaybooksConfigPage.tsx`
 *    already gives for omitting it from the Segmentation row's toolbar).
 *
 * ── Round-2-followup Unit A1 — Save-vs-Save-as-new-copy gate ───────────────
 * Same shared-edit leak the play-flow editor has (a Sequence mapped by more
 * than one rung/playbook renders its saved head everywhere at once, so a
 * plain Save from here silently overwrites what every one of them sees), and
 * the same fix: before either write ({@link handleSaveStep} or
 * {@link handleAddStep}) actually posts, `saveWithGate` reads
 * `sequenceQuery.data.activeMappingCount`. At `1` it saves exactly as before
 * (no dialog, ever — this must not become confirm-every-save for the
 * ordinary case). Above `1` it opens a confirm offering "Save" (today's
 * behavior — the shared head moves, every mapping sees it) or "Save as new
 * copy" (`duplicateSequenceWithEditedSteps`, `../duplicate-sequence-copy.ts`
 * — duplicates the Sequence and writes this edit onto the DUPLICATE only,
 * atomically as of round-2-followup-codex-fixes Unit B2).
 *
 * ── round-2-followup-codex-fixes plan, Unit B4 — follow-the-duplicate ──────
 * The Library page has no rung/mapping concept, so there is nothing HERE to
 * re-point at the new copy — but the ROW the rep was looking at in
 * `PlaybooksConfigPage.tsx`'s Sequence list still needs to. An earlier
 * version of this pane tried to solve that by flipping its OWN identity
 * (`activeSequenceId`, local state seeded from the `sequenceId` prop) onto
 * the duplicate — that state lived only inside this pane, so the row's name,
 * channel badge, step count, and kebab menu elsewhere in the list kept
 * describing the ORIGINAL sequence while the pane silently edited a
 * different one underneath them, a real split-identity bug (Codex
 * adversarial review, finding 5). It also never actually re-keyed anything:
 * the pane instance itself is still mounted under the ORIGINAL sequence's
 * `key` in the parent's `.map()`, so a rep who collapsed and re-expanded the
 * row landed right back on the original, edited copy nowhere to be found.
 *
 * Spencer's decision (2026-09-03, this plan's decision 4): the row should
 * FOLLOW the duplicate instead. This pane no longer tracks any identity of
 * its own — `sequenceId` (the prop) is used everywhere below, exactly as
 * before the broken flip-in-place attempt existed. Instead, `saveWithGate`'s
 * "Save as new copy" branch calls the new {@link SequenceCopyPaneProps.onSavedAsNewCopy}
 * prop with the duplicate's id and a synthesized `PlaybooksSequenceSummary`
 * row. The caller (`PlaybooksConfigPage.tsx`) does the actual re-keying: it
 * optimistically inserts that summary into the `library` query's cache (so a
 * row for the duplicate exists to expand at all, ahead of the real
 * invalidation-triggered refetch) and swaps `expandedSequenceIds` from the
 * original's id to the new one — collapsing the original's row and expanding
 * a FRESH `SequenceCopyPane` instance, keyed by the duplicate's own id, in
 * its place. The original Sequence's row is untouched and reappears,
 * collapsed, once the library refetch lands.
 */

export interface SequenceCopyPaneProps {
  sequenceId: string;
  /**
   * F2 (Batch 3, decision 10) — this page has no query of its own for the
   * sequence's channel (`getSequence`'s response doesn't carry it either;
   * only the Library summary row does), so the caller
   * (`PlaybooksConfigPage.tsx`'s Sequences row, which already renders a
   * channel badge off `seq.channel`) threads it straight in.
   */
  channel: LauncherChannel;
  /**
   * Round-2-followup-codex-fixes plan, Unit B4 (finding 5, "follow-the-
   * duplicate") — called once "Save as new copy" resolves, with the
   * duplicate's id and a `PlaybooksSequenceSummary` row synthesized from the
   * duplicate response (see this file's module doc). Fire-and-forget from
   * this pane's perspective: it is not awaited and its return value (if any)
   * is ignored. The caller owns re-keying the Library row onto the duplicate
   * — this pane has no identity of its own left to flip.
   */
  onSavedAsNewCopy: (newSequenceId: string, summary: PlaybooksSequenceSummary) => void;
}

export function SequenceCopyPane({ sequenceId, channel, onSavedAsNewCopy }: SequenceCopyPaneProps) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [isAddingStep, setIsAddingStep] = useState(false);
  // QA round 8 codex-fixes, U5b (F11) — the position of the step whose
  // "Remove step" click is in flight, so only that step's button disables
  // (mirrors `isAddingStep`, but keyed per-step since several buttons render
  // at once).
  const [isRemovingStep, setIsRemovingStep] = useState<number | null>(null);

  const sequenceQuery = useQuery({
    queryKey: sequenceDetailQueryKey(sequenceId),
    queryFn: () => getSequence(apiFetch, sequenceId),
  });

  /**
   * The Subject field's reply-step rule (2026-09-17), derived once for the
   * whole pane — above this component's `isLoading`/`isError` early returns,
   * so the hook order is stable.
   *
   * Gong REJECTS a subject on a reply step and 400s the ENTIRE enrollment, so
   * `send-payload.ts#toOverrideSteps` drops it and the editor must not offer
   * the field. Reply-ness is the step's ORDINAL in the position-sorted
   * sequence against the BOUND FLOW's live reply-step orders — the send's own
   * rule — never the step's own `gongStepDescriptor`, which is the imported
   * step's flag and goes stale on a reorder. See `gong-flow-picker-api.ts`'s
   * "reply-step contract" note.
   *
   * `play-flow/SequencesStep.tsx`'s `SequenceEditorShell` derives it through
   * the SAME hook + helper pair, so the two hosts cannot drift apart. `channel`
   * arrives as a prop here (this page has no channel query of its own) and the
   * flow binding comes off the sequence summary the detail response carries.
   */
  const gongReplyOrders = useGongReplyStepOrders(channel, sequenceQuery.data?.sequence.flowId ?? null);
  const headSteps = sequenceQuery.data?.head?.steps;
  const stepsInGongOrder = useMemo(() => sortStepsByPosition(headSteps ?? []), [headSteps]);
  /**
   * QA round 8 codex-fixes plan, U5b (F11) — a legacy Inflection sequence
   * saved before the one-step cap existed. The round-8 caption/hidden-
   * "Add step" logic below only ever compared against the cap with `>=`,
   * so a 2-step sequence rendered the same "…has its one step." sentence a
   * 1-step sequence gets — never true, and with no way to remove the extra
   * step, never fixable either (every save posts the whole array and the
   * server always 400s it, `api.ts#duplicateSequence`'s sibling gate at
   * `saveSequenceSteps`). `overCap` is `true` only strictly ABOVE the cap;
   * at or under the cap, this unit changes nothing.
   */
  const overCap = channel === 'inflection' && (headSteps?.length ?? 0) > INFLECTION_MAX_STEPS;

  /**
   * U9 — this sequence's staged "Generate with AI" review (U7a's
   * `generated-copy-store.ts`), scoped by the LIBRARY surface's own fixed
   * `'library'` scope (never a rung's `draftKey` — there is no rung here).
   * Read the same way `SequencesStep.tsx`'s `SequenceEditorShell` reads its
   * own rung-scoped entry.
   */
  const generation = useGeneratedCopyStore(selectGeneratedCopyEntry('library', sequenceId));
  /** U9 — whether the drawer is open for THIS exact library sequence. */
  const isGenerateSessionOpenHere = useGenerateWithAIStore(selectIsSessionOpenFor('library', sequenceId));
  /**
   * U9 — the `stagedGeneration` seedKey ingredient `SequenceStepEditor`'s
   * `review` prop requires, mirroring `SequencesStep.tsx`'s identical
   * bump-once-per-fresh-generation tracker (see that file's own doc for the
   * full rationale): bumps once per NEW `'pending'` token for this sequence,
   * never on the rep's own edit/decide keystrokes.
   */
  const [stagedGeneration, setStagedGeneration] = useState(0);
  const pendingToken = generation?.status === 'pending' ? generation.token : null;
  const lastPendingTokenRef = useRef<number | null>(null);
  useEffect(() => {
    lastPendingTokenRef.current = null;
  }, []);
  useEffect(() => {
    if (pendingToken === null || pendingToken === lastPendingTokenRef.current) return;
    lastPendingTokenRef.current = pendingToken;
    setStagedGeneration((n) => n + 1);
  }, [pendingToken]);

  function writeHeadToCache(
    targetSequenceId: string,
    response: { versionId: string; versionNumber: number | null; steps: SequenceStep[] },
  ) {
    queryClient.setQueryData(
      sequenceDetailQueryKey(targetSequenceId),
      (old: PlaybooksSequenceDetailResponse | undefined) =>
        old
          ? {
              ...old,
              head: {
                versionId: response.versionId,
                versionNumber: response.versionNumber,
                steps: response.steps,
              },
            }
          : old,
    );
  }

  /**
   * Unit A1 — the Save-vs-Save-as-new-copy gate, shared by both write call
   * sites below ({@link handleSaveStep}, {@link handleAddStep}).
   *
   * Round-3-QA-fixes plan, Unit C3 / decision 9 — every save fetches usage
   * FRESH (`staleTime: 0`) before deciding, and acknowledges the FRESH
   * count. R5-6/R5-7 (playbooks-qa-round5-feedback.md) — the gate below now
   * uses `libraryUsageGateNeedsConfirmation` (`places.length === 0`, with no
   * unsent plays, saves immediately with no dialog — this pane has no
   * self-place to discount, unlike the rung doorway's `<= 1`; see that
   * function's own doc), never confirm-every-save for a genuinely
   * unused/sole-use sequence. Above it, U2b rewrites this from an
   * `openAlertDialog` awaited by the click handler
   * onto the persistent "Where is this used" sidebar
   * (`usage-sidebar-store.ts`, U1) — identical mechanics to
   * `SequencesStep.tsx`'s own `saveWithGate` rewrite (U2a): a plain store
   * write (`openUsageSidebar`, never awaited) followed by a SYNCHRONOUS
   * throw of {@link UsageSidebarOpenedError}, so nothing here ever waits on
   * the rep's choice again. `use-usage-sidebar-decision.ts`'s hook, mounted
   * below in THIS component at this pane's own `library` origin, is what
   * actually executes "Update all"/"Save a new version" later, however long
   * after this call returns — see {@link handleUsageSidebarUpdateAll}/
   * {@link handleUsageSidebarSaveNew} for the execution half of both
   * choices, and this file's module doc ("follow-the-duplicate") for how
   * the PARENT ({@link SequenceCopyPaneProps.onSavedAsNewCopy}) still
   * re-keys the Library row onto a "Save a new version" duplicate.
   *
   * The sentinel must reach a TERMINAL catch un-decorated — `handleSaveStep`
   * below rethrows it after its own (harmless either way) query
   * invalidation, and `SequenceStepEditor.handleSave` is the terminal catch
   * that swallows it via `isUsageSidebarOpened`. `handleAddStep`'s own catch
   * (below) is a second terminal catch that must swallow it the same way —
   * it has no further caller to rethrow to.
   *
   * A `shared_mapping_conflict` (decision 3/9) invalidates the stale
   * sequence query and re-enters this whole function ONCE with freshly
   * refetched usage; a second consecutive conflict shows a plain toast
   * (never the server's raw `detail`) and rethrows so the editor keeps its
   * unsaved draft.
   */
  function saveWithGate(
    steps: SequenceSteps,
    generationInputs?: PlaybooksGenerationInputs,
  ): Promise<PlaybooksSaveSequenceStepsResponse> {
    const detail = sequenceQuery.data;
    const head = detail?.head;
    if (!detail || !head) {
      return Promise.reject(new Error("This sequence hasn't finished saving yet. Reload and try again."));
    }
    const sequenceName = detail.sequence.name;
    // Re-bind the narrowed values: TS does not carry `!detail || !head` narrowing
    // into the hoisted `attemptWithPlaces` function declaration below.
    const headVersionId = head.versionId;

    function attemptWithPlaces(
      places: PlaybooksUsagePlace[],
      ownVersionPlays: PlaybooksUsagePlay[],
      templatePlaybooks: PlaybooksTemplatePlaybook[],
    ): Promise<PlaybooksSaveSequenceStepsResponse> {
      // R5-6/R5-7 (playbooks-qa-round5-feedback.md) — this pane is
      // Library-only (no rung/mapping concept at all), so it always uses the
      // library threshold, unconditionally — see
      // `usage-gate.ts#libraryUsageGateNeedsConfirmation`'s own doc for why
      // this differs from `SequencesStep.tsx`'s rung-doorway `>1` rule.
      if (!libraryUsageGateNeedsConfirmation(places)) {
        return saveSequenceSteps(
          apiFetch,
          sequenceId,
          steps,
          headVersionId,
          places.length,
          generationInputs,
          distinctUnsentPlays(places),
        ).then((response) => {
          writeHeadToCache(sequenceId, response);
          return response;
        });
      }
      openUsageSidebarFromUsage(
        { places, ownVersionPlays, templatePlaybooks },
        // Plan 2026-09-28-006 U4 — the Library page edits a Library row
        // (never a play's copy); the doorway keeps its two-option footer.
        { kind: 'library', targetKind: 'sequence', id: sequenceId },
        { kind: 'sequence', id: sequenceId, name: sequenceName, owningPlayId: null, archivedInLibrary: false },
        {
          draftSteps: steps,
          headVersionId,
          generationInputs,
          // Codex fixes 2026-09-17, F2 — see `PendingUsageSidebar.stagedReview`.
          stagedReview: generationInputs !== undefined && generation?.status === 'ready' ? generation : undefined,
        },
      );
      throw new UsageSidebarOpenedError();
    }

    async function attempt(retried: boolean): Promise<PlaybooksSaveSequenceStepsResponse> {
      const usage = await queryClient.fetchQuery({
        queryKey: sequenceUsageQueryKey(sequenceId),
        queryFn: () => getSequenceUsage(apiFetch, sequenceId),
        staleTime: 0,
      });
      try {
        return await attemptWithPlaces(usage.places, usage.ownVersionPlays ?? [], usage.templatePlaybooks ?? []);
      } catch (error) {
        if (isSharedMappingConflictError(error) && !retried) {
          await queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
          return attempt(true);
        }
        if (isSharedMappingConflictError(error)) {
          // A SECOND consecutive conflict — decision 9's bounded retry gives
          // up. Throw a NEW error carrying only the plain text (never the
          // original conflict's raw server `detail`): both call sites above
          // this ({@link handleSaveStep}'s catch, then `SequenceStepEditor`'s
          // own generic catch; {@link handleAddStep}'s own catch directly)
          // surface `error.message` verbatim in their own toast, and
          // re-throwing the ORIGINAL error would let that raw `detail` leak
          // through even though this catch never showed it directly.
          throw new Error(
            "This sequence's places changed again while you were saving. Please try once more.",
          );
        }
        throw error;
      }
    }

    return attempt(false);
  }

  /**
   * U2b — the "Update all N" decision's execution, run by
   * `use-usage-sidebar-decision.ts`'s hook (below) at THIS pane's own
   * `library` origin, however long after `saveWithGate` opened the sidebar
   * and threw its sentinel. Mirrors `SequencesStep.tsx`'s
   * `handleUsageSidebarUpdateAll` — the Library page has no rung/draft-copy
   * store of its own to clear afterward, so the only work here is the write
   * itself and the cache update.
   */
  async function handleUsageSidebarUpdateAll(
    pending: {
      target: { id: string };
      draftSteps: SequenceSteps | null;
      headVersionId: string | null;
      places: readonly PlaybooksUsagePlace[];
      generationInputs?: PlaybooksGenerationInputs | null;
    },
    isStillCurrent: () => boolean,
  ): Promise<void> {
    const targetSequenceId = pending.target.id;
    const steps = pending.draftSteps ?? [];
    const response = await saveSequenceSteps(
      apiFetch,
      targetSequenceId,
      steps,
      pending.headVersionId!,
      pending.places.length,
      // terra-qyt.10 — mirrors `SequencesStep.tsx`'s own forwarding.
      pending.generationInputs ?? undefined,
      // Round-4 QA fixes, U5 (S1) / Decision 6 — the FRESH recompute, same
      // rule as `SequencesStep.tsx`'s own equivalent call.
      distinctUnsentPlays(pending.places),
    );
    // D15 (Codex F-B) — guard the client-side continuation, not the write.
    if (!isStillCurrent()) return;
    writeHeadToCache(targetSequenceId, response);
    // R5-3 (playbooks-qa-round5-feedback.md) — this write's OWN steps are now
    // baked into the shared head; drop the `library|<seqId>|<pos>` copy-draft
    // entries they came from, mirroring `SequencesStep.tsx`'s identical clear
    // in its own `handleUsageSidebarUpdateAll`. Before this fix, this pane
    // never cleared its drafts on the "Update all" branch, so a leftover
    // entry here made Config's (now-scoped) `useHasUnsavedWork('library')`
    // read dirty for a page the rep had already finished editing —
    // `clearCopyDraftIfUnchanged` is a no-op for any position the rep kept
    // typing on in the meantime, so it never clobbers newer, still-unsaved
    // work.
    for (const step of steps) {
      clearCopyDraftIfUnchanged(copyDraftKey('library', targetSequenceId, step.position), {
        subject: step.subject ?? '',
        body: step.body ?? '',
      });
    }
    // terra-qyt.10 — the staged "Generate with AI" review this write came
    // from (if any) is now baked into the head that just landed; see
    // `SequencesStep.tsx`'s own equivalent clear for the full reasoning.
    clearGeneratedCopy('library', targetSequenceId);
    addToast({ content: 'Step saved.', status: 'success' });
  }

  /**
   * U2b — the "Save a new version" decision's execution: the exact
   * duplicate-and-follow block the pre-sentinel gate dialog's own "Save a
   * new version" button ran inline (this file's module doc,
   * "follow-the-duplicate"), moved out of that dialog closure verbatim.
   * `usageCount: 0` is the already-fixed Codex adversarial-review finding
   * F5 override — a brand-new duplicate identity has zero places mapped to
   * it yet, regardless of how many places the ORIGINAL shared sequence has.
   *
   * U20 (M1, Codex round-2 "Might Break" #1) — `pending.headVersionId` is
   * forwarded as `duplicateSequenceWithEditedSteps`'s
   * `expectedSourceVersionId`, so a source head that moved since these steps
   * were edited/generated refuses the fork with a 409 `version_conflict`
   * (mirrors `SequencesStep.tsx`'s own `handleUsageSidebarSaveNew`). On that
   * conflict this handler invalidates the sequence detail cache and
   * RETHROWS — the hook's own `.catch` (`use-usage-sidebar-decision.ts`)
   * resets the sidebar to `'idle'` and toasts the server's detail sentence;
   * `onSavedAsNewCopy` is never called.
   */
  async function handleUsageSidebarSaveNew(
    pending: {
      target: { id: string };
      draftSteps: SequenceSteps | null;
      headVersionId: string | null;
      generationInputs?: PlaybooksGenerationInputs | null;
    },
    isStillCurrent: () => boolean,
  ): Promise<void> {
    const targetSequenceId = pending.target.id;
    const steps = pending.draftSteps ?? [];
    const sourceSummary = sequenceQuery.data?.sequence;
    // U17 (F6) — mirrors `handleUsageSidebarUpdateAll`'s own forwarding below:
    // the duplicate's v1 carries the same "Generate with AI" provenance
    // "Update all" already threads through `saveSequenceSteps`.
    let newSequenceId: string;
    let save: PlaybooksSaveSequenceStepsResponse;
    try {
      ({ newSequenceId, save } = await duplicateSequenceWithEditedSteps(
        apiFetch,
        targetSequenceId,
        steps,
        pending.generationInputs ?? undefined,
        pending.headVersionId ?? undefined,
      ));
    } catch (error) {
      if (isVersionConflictError(error) && isStillCurrent()) {
        void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(targetSequenceId) });
      }
      throw error;
    }
    // D15 (Codex F-B) — the duplicate write above is real either way; only
    // the continuations below (row re-key, library invalidate, toast) are
    // guarded against a request a later `openUsageSidebar` has replaced.
    if (!isStillCurrent()) return;
    // R5-3 (playbooks-qa-round5-feedback.md) — the OLD (source) sequence's
    // copy drafts are stale the instant the duplicate exists (this edit is
    // now baked into the duplicate's own head instead), mirroring
    // `SequencesStep.tsx`'s identical clear in its own
    // `handleUsageSidebarSaveNew`. No-op for any position the rep kept
    // typing on in the meantime.
    for (const step of steps) {
      clearCopyDraftIfUnchanged(copyDraftKey('library', targetSequenceId, step.position), {
        subject: step.subject ?? '',
        body: step.body ?? '',
      });
    }
    // terra-qyt.10 — mirrors `SequencesStep.tsx`'s own save-new clear: the
    // approved copy is already baked into the duplicate's head.
    clearGeneratedCopy('library', targetSequenceId);
    void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
    const summary: PlaybooksSequenceSummary = {
      ...sourceSummary!,
      id: newSequenceId,
      name: `${sourceSummary!.name} (copy)`,
      headVersionId: save.versionId,
      headVersionNumber: save.versionNumber,
      stepCount: steps.length,
      usageCount: 0,
    };
    onSavedAsNewCopy(newSequenceId, summary);
    addToast({ content: `Saved a new version, "${summary.name}".`, status: 'success' });
  }

  /**
   * U2b — mounted once per `SequenceCopyPane` instance, matching THIS
   * pane's own `library` origin (`originKeyOf`, `usage-sidebar-store.ts`) —
   * see `use-usage-sidebar-decision.ts`'s module doc for the StrictMode /
   * conflict-retry mechanics this hides.
   *
   * Gated on `mode === 'save'` (rather than the raw, unconditional
   * `originKeyOf` a `play` origin's equivalent hook uses) because a
   * `library` origin's key collides on identity alone: THIS sequence's own
   * delete (`PlaybooksConfigPage.tsx`'s own `useUsageSidebarDecision` call)
   * computes the exact same `library:sequence:<id>` key when it is the one
   * currently pending. Without this gate, whichever of the two hooks'
   * effects happened to run first would wrongly "take" the other's
   * decision — this pane has no `delete` handler, so it would silently
   * swallow a delete with a `no-handler` outcome instead of ever executing
   * it. Passing `''` (matches nothing real) whenever the pending request
   * isn't a `save` keeps this pane's hook inert for any delete targeting
   * its own sequence.
   *
   * Playbooks QA round 6, U7 (item 3) — the SAME collision exists for a
   * SENDER save: `SequenceSenderPopover`'s Library row opens under this
   * exact `library:sequence:<id>` origin key too (Decision 7 reuses
   * `target.kind: 'sequence'`, no new target kind), and THIS pane's
   * `updateAll`/`saveNew` handlers are for STEPS — a handler that returns
   * early is reported as success by `usage-sidebar-decision-logic.ts` and
   * would close the sidebar having written nothing. `PlaybooksConfigPage.tsx`
   * owns every sender pending on this origin (its own `configOriginKey`
   * gained a matching branch), so this pane's hook must stay inert for one
   * too — same `''` trick, gated on `draftSettings === undefined` (plan
   * 2026-09-28-006 U4: the sender-only `draftSender` became `draftSettings`).
   */
  const pendingUsageSidebarMode = useUsageSidebarStore((state) => state.pending?.mode ?? null);
  const pendingIsSenderSave = useUsageSidebarStore((state) => state.pending?.draftSettings !== undefined);
  useUsageSidebarDecision(
    pendingUsageSidebarMode === 'save' && !pendingIsSenderSave
      ? originKeyOf({ kind: 'library', targetKind: 'sequence', id: sequenceId })
      : '',
    {
      updateAll: handleUsageSidebarUpdateAll,
      saveNew: handleUsageSidebarSaveNew,
      resumeReview: async (pending) => {
        resumeReviewFromPending(pending);
      },
    },
  );

  /** Mirrors `SequencesStep.tsx`'s `handleSaveStep` main-line branch — see this file's module doc. */
  async function handleSaveStep(
    position: number,
    values: { subject: string; body: string },
  ): Promise<SequenceStep> {
    const head = sequenceQuery.data?.head;
    if (!head) {
      throw new Error("This sequence hasn't finished saving yet. Reload and try again.");
    }
    const steps = head.steps.map((s) =>
      s.position === position ? { ...s, subject: values.subject, body: values.body } : s,
    );
    try {
      const response = await saveWithGate(steps);
      const saved = response.steps.find((s) => s.position === position);
      if (!saved) throw new Error('Saved, but the step is missing from the response — reload to confirm.');
      return saved;
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
      await queryClient.invalidateQueries({ queryKey: sequenceUsageQueryKey(sequenceId) });
      throw error;
    }
  }

  /**
   * U2b (bead-notes regression guard) — `isAddingStep` is cleared by the
   * `finally` below regardless of how this catch handles the error, so the
   * button re-enables either way; the early return here exists ONLY to
   * suppress the sentinel's own (meaningless) error toast — the sentinel is
   * not a real failure, `saveWithGate` already published the pending save
   * to the sidebar.
   */
  async function handleAddStep() {
    const head = sequenceQuery.data?.head;
    // Gong's flow fixes the step count — see the render below; belt-and-suspenders for a stale click.
    // QA round 8, item 6 — Inflection is capped at one step (a second could
    // never be delivered — the send only ever reads step 1); same
    // belt-and-suspenders stance for a stale click past the cap.
    if (!head || channel === 'gong_engage' || (channel === 'inflection' && head.steps.length >= INFLECTION_MAX_STEPS)) {
      return;
    }
    setIsAddingStep(true);
    try {
      const steps = [...head.steps, { position: head.steps.length, subject: '', body: '' }];
      await saveWithGate(steps);
      addToast({ content: 'Step added.', status: 'success' });
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
      await queryClient.invalidateQueries({ queryKey: sequenceUsageQueryKey(sequenceId) });
      if (isUsageSidebarOpened(error)) return;
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not add a step — try again.',
      });
    } finally {
      setIsAddingStep(false);
    }
  }

  /**
   * QA round 8 codex-fixes, U5b (F11) — the Library-only repair path for a
   * legacy over-cap Inflection sequence: drop the given position and
   * renumber the rest from 0 (the send sorts on `position`, and the cap
   * itself counts array length, not any position value), then run the SAME
   * gated save every other write in this pane uses. Error handling mirrors
   * {@link handleAddStep}'s catch exactly — a second terminal catch for the
   * same sentinel, since this has no further caller to rethrow to.
   */
  async function handleRemoveStep(position: number) {
    const head = sequenceQuery.data?.head;
    if (!head) return;
    setIsRemovingStep(position);
    try {
      const remaining = sortStepsByPosition(head.steps)
        .filter((s) => s.position !== position)
        .map((s, i) => ({ ...s, position: i }));
      await saveWithGate(remaining);
      addToast({ content: 'Step removed.', status: 'success' });
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
      await queryClient.invalidateQueries({ queryKey: sequenceUsageQueryKey(sequenceId) });
      if (isUsageSidebarOpened(error)) return;
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not remove the step — try again.',
      });
    } finally {
      setIsRemovingStep(null);
    }
  }

  if (sequenceQuery.isLoading) {
    return (
      <div className="flex justify-center p-6">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (sequenceQuery.isError || !sequenceQuery.data) {
    return <Alert status="error">Couldn&apos;t load this Sequence&apos;s steps. Please try again.</Alert>;
  }

  const head = sequenceQuery.data.head;

  return (
    <div className="flex flex-col gap-3">
      {head === null || head.steps.length === 0 ? (
        <Text size="sm" variant="tertiary">
          No steps on this Sequence yet.
        </Text>
      ) : (
        stepsInGongOrder.map((step) => {
          // D12 — this step's staged review (U7a's `StepReview`), or
          // `undefined` for ordinary hand-edit mode. Widened from
          // U9's original `status === 'ready'`-only check: a `'pending'`
          // entry ALSO stages per-step reviews progressively as SSE frames
          // arrive (`generated-copy-store.ts`'s own doc — "each step card
          // switches into review mode as its own step frame arrives"), and
          // `SequenceStepEditor`'s `review` prop is what hides this step's
          // hand-edit Save row (decisions 2/12) — so a step whose frame has
          // already landed mid-generation must hide Save too, not just once
          // the WHOLE generation reaches `'ready'`. A `'error'` entry (D5) has
          // no reviews to show — same as no entry at all.
          const stepReview =
            generation?.status === 'ready' || generation?.status === 'pending'
              ? generation.steps[step.position]
              : undefined;
          return (
            <div key={step.position} className="flex flex-col gap-1">
              {/*
                QA round 8 codex-fixes, U5b (F11) — gated on `overCap` so an
                at-or-under-cap sequence (the overwhelming majority) renders
                byte-identical to round 8. Wraps the editor rather than
                threading a prop into `SequenceStepEditor` — this control
                belongs to the Library host's over-cap repair flow, not the
                editor's own step-authoring surface.
              */}
              {overCap && (
                <div className="flex justify-end">
                  <Button
                    variant="inline-text"
                    size="xs"
                    onClick={() => void handleRemoveStep(step.position)}
                    loading={isRemovingStep === step.position}
                  >
                    Remove step
                  </Button>
                </div>
              )}
              <SequenceStepEditor
                step={step}
                // QA round 7, U5 (item 7) — the position-sorted list's 1-based
                // index, matching `toOverrideSteps`/`DispatchInspector`.
                ordinal={stepOrdinal(stepsInGongOrder, step.position)}
                sequenceId={sequenceId}
                // D1 shared-edit-leak fix — the Library page has no rung/Play
                // concept, so every draft typed here shares this one fixed scope.
                // It stays isolated from every play-flow rung's own scope by
                // construction: a rung's scope is always `rung:<id>`/`new:<n>`,
                // never the literal string `'library'`.
                draftScope="library"
                renderedVersionId={head.versionId}
                // Gong 400s the WHOLE enrollment over a subject on a reply step,
                // so the field is not offered for one. Ordinal-against-the-flow,
                // the send's own rule — see `gongReplyOrders` above.
                isReplyStep={isReplyAtOrdinal(stepsInGongOrder, step.position, gongReplyOrders)}
                onSave={handleSaveStep}
                // F2 (Batch 3, decision 10) — Gong bodies are HTML, but not every
                // step ON a Gong sequence's body is REAL HTML shaped (a
                // CALL/LINKEDIN_*/CUSTOM step can carry a plain-text script).
                // Plan A2 (`2026-09-03-001-launcher-v2-round2-followup-plan.md`),
                // hybrid rule: `gongStepBodyFormat` treats email-family steps
                // (`step.gongStepDescriptor.step_type`) as unconditionally rich,
                // and promotes any OTHER step type to rich only when its own
                // CURRENT `body` content is sniffed as Gong's real HTML envelope
                // shape — see that function's doc for the full rule. A plain
                // manually-typed body (or no body at all) on a non-email step
                // always degrades to `'plain'`, never guessed HTML-safe.
                bodyFormat={
                  channel === 'gong_engage' ? gongStepBodyFormat(step.body, step.gongStepDescriptor) : 'plain'
                }
                // D22 (Codex F-K) — the hand-edit Save/Discard row is
                // suppressed for the WHOLE library Generate session
                // (configure, pending, review), not only once an individual
                // step's own review frame has arrived. Before that fix,
                // `review` (below) being `undefined` until this exact
                // position's frame landed left an ordinary Save visible for
                // every not-yet-arrived step, and for every step at all
                // before the rep had even clicked Generate — contradicting
                // D12's "whole session" requirement.
                handSaveSuppressed={isGenerateSessionOpenHere}
                // U9 (d12) — hides the ordinary hand-edit Save row for the
                // duration of a review, exactly like `SequencesStep.tsx`'s own
                // rung editors; a row with no staged review (`stepReview`
                // `undefined`) is unaffected and still shows Save.
                review={
                  stepReview
                    ? {
                        entry: stepReview,
                        stagedGeneration,
                        onEdit: (patch) => editStagedStep('library', sequenceId, step.position, patch),
                        onDecide: (decision) => decideStagedStep('library', sequenceId, step.position, decision),
                      }
                    : undefined
                }
                // U9b (R10) — this page has no rung/campaign/sample-member
                // context at all (this file's module doc, "EXPLICITLY DEFERRED
                // FROM THIS PANE"), so every picker row here renders "No
                // sample" — an explicit `null`, not an omission, so a future
                // reader doesn't mistake this for a forgotten wiring.
                sampleMember={null}
                campaign={null}
                sender={null}
              />
            </div>
          );
        })
      )}
      {channel === 'gong_engage' ? (
        // 2026-09-17 QA follow-ups, U5 — Gong owns this sequence's step
        // structure (the flow's real steps, imported), so adding a step here
        // would grow the sequence past the bound flow and only be refused
        // later, at bind/send. Not rendered-disabled: there is nothing a rep
        // could do on this page to unlock it (same stance as the hand-edit
        // Subject on a reply step).
        <Text size="xs" variant="tertiary">
          Gong sets this sequence&apos;s steps — add or remove them in the Gong flow.
        </Text>
      ) : overCap ? (
        // QA round 8 codex-fixes, U5b (F11) — a legacy sequence saved before
        // the one-step cap, strictly ABOVE it. Unlike the at-cap caption
        // below, this one is honest about the count and points the rep at
        // the fix now on screen (the "Remove step" buttons rendered per
        // step, above) rather than claiming the sequence already has its
        // one step when it does not.
        <Text size="xs" variant="tertiary">
          Inflection sends one email — this sequence has {head?.steps.length ?? 0} steps. Remove the extra ones
          before it can be saved or sent.
        </Text>
      ) : channel === 'inflection' && (head?.steps.length ?? 0) >= INFLECTION_MAX_STEPS ? (
        // QA round 8, item 6 — Inflection sends exactly one email
        // (`send-payload.ts` only ever reads the first override step), so a
        // second step could never be delivered even if saved. Same stance as
        // the Gong caption above: not rendered-disabled, there is nothing a
        // rep could do here to unlock it.
        <Text size="xs" variant="tertiary">
          Inflection sends one email — this sequence has its one step.
        </Text>
      ) : (
        <div className="flex justify-start">
          <Button variant="secondary" size="sm" onClick={() => void handleAddStep()} loading={isAddingStep}>
            <Icon type={Plus} />
            Add step
          </Button>
        </div>
      )}
      {/*
        U9 — the "Generate with AI" drawer, mounted here (not on the
        `PlaybooksConfigPage.tsx` row) exactly like `SequencesStep.tsx`
        mounts it next to its own per-rung editor: it needs this sequence's
        `head.steps` as the Generate scaffold and its `sequence.name`/
        `generationPreset` for the campaign preselect, both of which only
        this pane's own `sequenceQuery` has. `campaign`/`rungConditions` are
        `null` — the library surface has no fixed campaign and no rung
        audience to default from.
      */}
      {isGenerateSessionOpenHere && (
        <GenerateWithAIDrawer
          scope="library"
          sequenceId={sequenceId}
          surface="library"
          campaign={null}
          rungConditions={null}
          headSteps={head?.steps ?? []}
          headVersionId={head?.versionId ?? null}
          onClose={closeGenerateSession}
          campaignPicker={
            <GenerateCampaignPicker
              presetCampaignSfdcId={sequenceQuery.data.generationPreset?.campaignSfdcId ?? null}
            />
          }
          sequenceName={sequenceQuery.data.sequence.name}
          usageCount={sequenceQuery.data.sequence.usageCount}
          onApply={(steps, generationInputs) => saveWithGate(steps, generationInputs)}
        />
      )}
    </div>
  );
}
